/**
 * mymemo PC 앱(트레이 에이전트) 설치 버튼 — 윈도 PC 브라우저에서만 보인다. 앱 안에서 연 화면이면 숨긴다.
 * 설치 파일은 서버 /api/onto/memo/agent/download (로그인 없이 받음, 비밀 아님).
 */
import React, { useEffect, useRef, useState } from "react";
import { Download, MonitorDown, X } from "lucide-react";
import { formatBytes } from "./memoApi";
import QrCode, { MOBILE_INSTALL_URL } from "../components/QrCode";

export const IN_AGENT_KEY = "loginhs:in-agent";

/** 트레이 앱이 화면을 열 때 주소에 agent=1 을 붙인다 — 그 뒤로는 이 브라우저(앱의 웹뷰)를 앱으로 기억한다. */
export function rememberAgentFlag() {
  try {
    if (new URLSearchParams(window.location.search).get("agent") === "1") localStorage.setItem(IN_AGENT_KEY, "1");
  } catch { /* 저장소를 못 쓰면 그냥 버튼을 보인다 */ }
}

function inAgent() {
  try { return localStorage.getItem(IN_AGENT_KEY) === "1"; } catch { return false; }
}

function isWindowsDesktop() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  return /Windows NT/i.test(ua) && !/Mobile/i.test(ua);
}

export default function AgentInstall() {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const boxRef = useRef(null);
  const show = isWindowsDesktop() && !inAgent();

  useEffect(() => {
    if (!open || info) return;
    fetch("/api/onto/memo/agent").then((r) => (r.ok ? r.json() : { available: false }))
      .then(setInfo).catch(() => setInfo({ available: false }));
  }, [open, info]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); window.removeEventListener("keydown", onKey); };
  }, [open]);

  if (!show) return null;
  return (
    <div className="mm-agent" ref={boxRef}>
      <button type="button" className="mm-plain mm-agent-btn" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid="agent-install">
        <MonitorDown size={14} /> PC 앱 설치
      </button>
      {open && (
        <div className="mm-agent-pop" role="dialog" aria-label="mymemo PC 앱 설치">
          <div className="mm-agent-top">
            <b>mymemo PC 앱</b>
            <button type="button" className="mm-icon" onClick={() => setOpen(false)} aria-label="닫기"><X size={14} /></button>
          </div>
          <ul className="mm-agent-feat">
            <li>작업 표시줄 트레이에 늘 떠 있습니다</li>
            <li><span className="mm-key">Ctrl</span>+<span className="mm-key">Alt</span>+<span className="mm-key">M</span> 어디서든 빠른 메모</li>
            <li>세번을 복사해 두고 열면 세번 칸이 채워집니다</li>
          </ul>
          {info === null ? (
            <div className="mm-faint">확인하는 중…</div>
          ) : info.available ? (
            <a className="mm-new mm-agent-dl" href={info.url} download data-testid="agent-download">
              <Download size={14} /> 내려받기 <span className="mm-agent-size">Windows · {formatBytes(info.size)}{info.version ? ` · v${info.version}` : ""}</span>
            </a>
          ) : (
            <div className="mm-faint">설치 파일을 준비하고 있습니다.</div>
          )}
          <ol className="mm-agent-steps">
            <li>받은 파일을 실행합니다.</li>
            <li>'Windows의 PC 보호' 창이 뜨면 <b>추가 정보 → 실행</b>을 누릅니다. <span className="mm-faint">(코드 서명 준비 중)</span></li>
            <li>열린 mymemo 창에서 처음 한 번 로그인합니다.</li>
          </ol>
          <div className="mm-agent-phone">
            <QrCode value={MOBILE_INSTALL_URL} size={84} label="휴대폰 설치 안내 QR" />
            <div><b>휴대폰에도 설치</b><br />카메라로 찍으면 설치 안내가 열립니다. 같은 메모를 휴대폰에서 봅니다.</div>
          </div>
        </div>
      )}
    </div>
  );
}
